import { useEffect, useRef } from "react";
import PlayRecordService from "../../../../services/PlayRecordService";

/** Defer LEAVE so React Strict Mode remount can cancel the spurious cleanup leave. */
const LEAVE_DEFER_MS = 200;

/** Active visit per puzzle key (survives Strict Mode double-mount in the same tab). */
const activeVisits = new Map();

/** Pending deferred leave timers keyed by puzzle. */
const pendingLeaves = new Map();

function visitKey(puzzleType, puzzleId) {
    return `${String(puzzleType).trim()}:${String(puzzleId).trim()}`;
}

function cancelPendingLeave(key) {
    const timer = pendingLeaves.get(key);
    if (timer != null) {
        clearTimeout(timer);
        pendingLeaves.delete(key);
    }
}

/**
 * POST /home/play-record/enter on mount and /home/play-record/leave on unmount + pagehide.
 * Works logged-in or anonymous (clientSessionId in localStorage). See puzzle-be/docs/play-record-rest-api.md.
 *
 * @param {{ puzzleType?: string|null, puzzleId?: string|null, enabled?: boolean }} options
 */
export default function usePlayRecord({ puzzleType, puzzleId, enabled = true }) {
    const leaveSentRef = useRef(false);

    useEffect(() => {
        leaveSentRef.current = false;

        if (!enabled || !puzzleType) {
            return undefined;
        }
        const pid = puzzleId ? String(puzzleId).trim() : "";
        if (!pid) {
            return undefined;
        }

        const type = String(puzzleType).trim();
        const key = visitKey(type, pid);

        // Strict Mode: remount cancels the deferred leave from the first cleanup.
        cancelPendingLeave(key);

        let visitId = activeVisits.get(key)?.visitId;
        if (!visitId) {
            visitId = PlayRecordService.newVisitId();
            activeVisits.set(key, { visitId });
            PlayRecordService.recordEnter({
                visitId,
                puzzleType: type,
                puzzleId: pid,
            });
        }

        const payload = { visitId, puzzleType: type, puzzleId: pid };

        const sendLeave = (useBeacon = false) => {
            if (leaveSentRef.current) return;
            leaveSentRef.current = true;
            cancelPendingLeave(key);
            activeVisits.delete(key);
            PlayRecordService.recordLeave({ ...payload, useBeacon });
        };

        const scheduleLeave = (useBeacon = false) => {
            cancelPendingLeave(key);
            const timer = setTimeout(() => {
                pendingLeaves.delete(key);
                sendLeave(useBeacon);
            }, LEAVE_DEFER_MS);
            pendingLeaves.set(key, timer);
        };

        const onPageHide = () => sendLeave(true);
        window.addEventListener("pagehide", onPageHide);

        return () => {
            window.removeEventListener("pagehide", onPageHide);
            scheduleLeave(false);
        };
    }, [enabled, puzzleType, puzzleId]);
}
